// File: frontend/src/pages/Contacts/ContactsPage.jsx
// Contacts management page with 360-degree view

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { ExportContactsButton } from '../../components/Export/ExportButton';
import {
  UserIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  StarIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import ContactForm from '../../components/Contacts/ContactForm';
import { TagsFilter } from '../../components/Tags/TagManager';
import { useAvailableTags } from '../../hooks/useTags';
import ContactListItem from '../../components/Contacts/ContactListItem';
import { formatLifecycleStage, formatStatus } from '../../components/Contacts/contactFormat';
import { isHighScore } from '../../utils/leadScore';

const ContactsPage = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLifecycle, setSelectedLifecycle] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [sortBy, setSortBy] = useState('last_activity');

  // Fetch available tags for filtering
  const { tags: availableTags, loading: tagsLoading } = useAvailableTags();

  // Fetch contacts data
  const { data: contactsData, isLoading, error } = useQuery(
    ['contacts', { search: searchTerm, page: currentPage, pageSize, lifecycle: selectedLifecycle, status: selectedStatus, company: selectedCompany, tags: selectedTags, sortBy }],
    async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
      });

      if (searchTerm) params.append('search', searchTerm);
      if (selectedLifecycle) params.append('lifecycle_stage', selectedLifecycle);
      if (selectedStatus) params.append('lead_status', selectedStatus);
      if (selectedCompany) params.append('company_id', selectedCompany);
      if (selectedTags.length > 0) params.append('tags', selectedTags.join(','));
      params.append('sort_by', sortBy);
      params.append('sort_order', sortBy === 'first_name' ? 'asc' : 'desc');
      
      const response = await api.get(`/contacts?${params}`);
      return response.data;
    },
    {
      enabled: !!user?.id,
      keepPreviousData: true,
    }
  );

  // Fetch companies for filter
  const { data: companies } = useQuery(
    'companies',
    async () => {
      const response = await api.get('/companies?limit=100');
      return response.data.companies || [];
    },
    { enabled: !!user?.id }
  );

  // Delete contact mutation
  const deleteMutation = useMutation(
    (contactId) => api.delete(`/contacts/${contactId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('contacts');
        toast.success('Contato excluído com sucesso!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao excluir contato');
      },
    }
  );

  const contacts = contactsData?.contacts || [];
  const totalContacts = contactsData?.total || 0;
  const totalPages = Math.ceil(totalContacts / pageSize);

  // Lifecycle stages and statuses for filters
  const lifecycleStages = [
    'subscriber',
    'lead', 
    'marketing_qualified_lead',
    'sales_qualified_lead',
    'opportunity',
    'customer',
    'evangelist'
  ];

  const leadStatuses = [
    'new',
    'contacted',
    'qualified',
    'unqualified',
    'follow_up',
    'meeting_scheduled',
    'proposal_sent',
    'negotiation',
    'closed_won',
    'closed_lost'
  ];

  const sortOptions = [
    { value: 'last_activity', label: 'Última atividade' },
    { value: 'lead_score', label: 'Score' },
    { value: 'created_at', label: 'Mais recente' },
    { value: 'first_name', label: 'Nome' },
  ];

  const handleDeleteContact = (contact) => {
    if (window.confirm(`Tem certeza que deseja excluir o contato ${contact.first_name} ${contact.last_name}?`)) {
      deleteMutation.mutate(contact.id);
    }
  };

  const handleEditContact = (contact) => {
    setSelectedContact(contact);
    setShowEditModal(true);
  };

  const handleCloseModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setSelectedContact(null);
  };

  // Handle tag filter changes
  const handleTagsChange = (tags) => {
    setSelectedTags(tags);
    setCurrentPage(1); // Reset to page 1 when filtering
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              Gestão de Contatos
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Vista de 360 graus dos seus contatos e relacionamentos
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <ExportContactsButton
              variant="secondary"
              size="md"
              filters={{ lead_status: selectedStatus || undefined }}
            />
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Novo Contato</span>
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 sm:space-x-4">
          {/* Search */}
          <div className="flex-1 max-w-lg">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nome, email, telefone ou empresa..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1); // Reset to page 1 when searching
                }}
                className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Sort selector — siempre visible para que el orden nunca sea un misterio */}
          <div className="flex items-center space-x-2">
            <label htmlFor="contacts-sort" className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
              Ordenar por
            </label>
            <select
              id="contacts-sort"
              value={sortBy}
              onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              {sortOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Filter toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-950"
          >
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <span>Filtros</span>
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
            {/* Tags filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🏷️ Filtrar por Tags
              </label>
              <TagsFilter
                selectedTags={selectedTags}
                onTagsChange={handleTagsChange}
                availableTags={availableTags}
                placeholder="Selecionar tags para filtrar..."
                multiple={true}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estágio do Ciclo
                </label>
                <select
                  value={selectedLifecycle}
                  onChange={(e) => {
                    setSelectedLifecycle(e.target.value);
                    setCurrentPage(1); // Reset to page 1 when filtering
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Todos os estágios</option>
                  {lifecycleStages.map(stage => (
                    <option key={stage} value={stage}>{formatLifecycleStage(stage)}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={selectedStatus}
                  onChange={(e) => {
                    setSelectedStatus(e.target.value);
                    setCurrentPage(1); // Reset to page 1 when filtering
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Todos os status</option>
                  {leadStatuses.map(status => (
                    <option key={status} value={status}>{formatStatus(status)}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Empresa
                </label>
                <select
                  value={selectedCompany}
                  onChange={(e) => {
                    setSelectedCompany(e.target.value);
                    setCurrentPage(1); // Reset to page 1 when filtering
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Todas as empresas</option>
                  {companies?.map(company => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <UserIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Contatos</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">{totalContacts}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <StarIcon className="h-8 w-8 text-yellow-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">High Score</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {contacts?.filter((c) => isHighScore(c.lead_score)).length || 0}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <CalendarIcon className="h-8 w-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Ativo 7 dias</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {contacts?.filter(c => {
                  const lastActivity = new Date(c.updated_at);
                  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
                  return lastActivity > weekAgo;
                }).length || 0}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <CurrencyDollarIcon className="h-8 w-8 text-purple-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Valor Total</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                R$ {(contacts?.reduce((sum, c) => sum + (c.potential_value || 0), 0) / 1000).toFixed(0)}k
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Contacts List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Contatos ({totalContacts})
          </h3>
        </div>
        
        {isLoading ? (
          <div className="p-6">
            <div className="animate-pulse space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <div className="text-red-600 mb-4">⚠️</div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Erro ao carregar contatos
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              {error.response?.data?.message || 'Tente novamente mais tarde'}
            </p>
          </div>
        ) : contacts.length === 0 ? (
          <div className="p-6 text-center">
            <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Nenhum contato encontrado
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              Tente ajustar os filtros ou criar um novo contato.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {contacts.map((contact) => (
              <ContactListItem
                key={contact.id}
                contact={contact}
                onEdit={handleEditContact}
                onDelete={handleDeleteContact}
                deleteLoading={deleteMutation.isLoading}
              />
            ))}
          </div>
        )}
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 sm:px-6 py-4 border-t border-gray-200">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-gray-700">
                Mostrando {((currentPage - 1) * pageSize) + 1} a {Math.min(currentPage * pageSize, totalContacts)} de {totalContacts} contatos
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:bg-gray-950"
                >
                  Anterior
                </button>
                <span className="px-3 py-1 text-sm">
                  {currentPage} de {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:bg-gray-950"
                >
                  Próximo
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create Contact Modal */}
      {showCreateModal && (
        <ContactForm
          isOpen={showCreateModal}
          onClose={handleCloseModals}
          onSuccess={() => {
            queryClient.invalidateQueries('contacts');
            handleCloseModals();
          }}
        />
      )}

      {/* Edit Contact Modal */}
      {showEditModal && selectedContact && (
        <ContactForm
          isOpen={showEditModal}
          onClose={handleCloseModals}
          contact={selectedContact}
          onSuccess={() => {
            queryClient.invalidateQueries('contacts');
            handleCloseModals();
          }}
        />
      )}
    </div>
  );
};

export default ContactsPage;